import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { User, MapPin, Phone } from 'lucide-react';
import { PROVINCES, DISTRICTS, WARDS } from '@/constants/orderConstants';

interface RecipientDeliverySectionProps {
  formData: {
    recipient: string;
    recipientPhone: string;
    address: string;
    detailedAddress: string;
    addressType: 'new' | 'old';
    province: string;
    district: string;
    ward: string;
  };
  mockAddresses: any[];
  showAddressDialog: boolean;
  onInputChange: (field: string, value: string) => void;
  onAddressSelect: (address: any) => void;
  setShowAddressDialog: (show: boolean) => void;
}

/**
 * Component gộp thông tin người nhận và địa chỉ giao hàng
 * Tối ưu hiển thị trong một card duy nhất
 */
const RecipientDeliverySection: React.FC<RecipientDeliverySectionProps> = ({
  formData,
  mockAddresses,
  showAddressDialog,
  onInputChange,
  onAddressSelect,
  setShowAddressDialog
}) => {
  return (
    <Card className="border-l-4 border-l-orange-500 shadow-sm">
      <CardHeader className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-950/30 dark:to-orange-900/20 py-2">
        <CardTitle className="text-sm text-orange-900 dark:text-orange-100 flex items-center">
          <User className="h-4 w-4 text-orange-600 dark:text-orange-400 mr-2" />
          Người nhận & Địa chỉ giao hàng
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-3 space-y-3">
        {/* Tên người nhận và Số điện thoại trên cùng một dòng */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="recipient" className="text-sm">Tên người nhận *</Label>
            <Input
              id="recipient"
              value={formData.recipient}
              onChange={(e) => onInputChange('recipient', e.target.value)}
              placeholder="Nhập tên người nhận"
              className="h-9"
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="recipientPhone" className="text-sm">Số điện thoại *</Label>
            <Input
              id="recipientPhone"
              value={formData.recipientPhone}
              onChange={(e) => onInputChange('recipientPhone', e.target.value)}
              placeholder="Nhập số điện thoại"
              className="h-9"
              required
            />
          </div>
        </div>
          
        {/* Quick Actions - Thu gọn */}
        <div className="flex gap-2">
          <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs">
                <MapPin className="h-3 w-3 mr-1" />
                Chọn từ danh sách
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-orange-500" />
                  Chọn địa chỉ có sẵn
                </DialogTitle>
              </DialogHeader>
              <Tabs defaultValue="recipient">
                <TabsList>
                  <TabsTrigger value="recipient">Người nhận</TabsTrigger>
                </TabsList>
                <TabsContent value="recipient" className="space-y-3">
                  {mockAddresses.filter(addr => addr.type === 'recipient').map((address) => (
                    <Card 
                      key={address.id} 
                      className="cursor-pointer hover:shadow-md hover:border-orange-300 transition-all duration-200"
                      onClick={() => onAddressSelect(address)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-orange-900">{address.name}</span>
                          {address.isDefault && (
                            <Badge variant="secondary" className="bg-orange-100 text-orange-700">Mặc định</Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <div className="flex items-center gap-2">
                            <Phone className="h-3 w-3" />
                            {address.phone}
                          </div>
                          <div className="flex items-start gap-2">
                            <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
                            <span>{address.detailedAddress}, {address.ward}, {address.district && address.district + ', '}{address.province}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
          
          <Button variant="outline" size="sm" className="h-8 text-xs">
            <Phone className="h-3 w-3 mr-1" />
            Gọi điện
          </Button>
        </div>

        {/* Địa chỉ giao hàng - Tối ưu layout */}
        <div className="space-y-3">
          {/* Loại địa chỉ */}
          <div className="space-y-1">
            <Label className="text-sm">Loại địa chỉ</Label>
            <RadioGroup
              value={formData.addressType}
              onValueChange={(value: 'new' | 'old') => onInputChange('addressType', value)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="new" id="new" />
                <Label htmlFor="new" className="text-sm">Địa chỉ mới</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="old" id="old" />
                <Label htmlFor="old" className="text-sm">Địa chỉ cũ</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Tỉnh/TP, Quận/Huyện, Phường/Xã trên cùng một dòng */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="province" className="text-sm font-medium flex items-center gap-1">
                Tỉnh/TP 
                <span className="text-red-500">*</span>
              </Label>
              <Select value={formData.province} onValueChange={(value) => onInputChange('province', value)}>
                <SelectTrigger className="h-11 border-orange-200 focus:border-orange-400 focus:ring-orange-400/20">
                  <SelectValue placeholder="Chọn tỉnh/thành phố" />
                </SelectTrigger>
                <SelectContent>
                  {PROVINCES.map((province) => (
                    <SelectItem key={province.value} value={province.value}>
                      {province.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {formData.addressType === 'old' && (
              <div className="space-y-2">
                <Label htmlFor="district" className="text-sm font-medium flex items-center gap-1">Quận/Huyện</Label>
                <Select value={formData.district} onValueChange={(value) => onInputChange('district', value)}>
                  <SelectTrigger className="h-11 border-orange-200 focus:border-orange-400 focus:ring-orange-400/20">
                    <SelectValue placeholder="Chọn quận/huyện" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.province && DISTRICTS[formData.province as keyof typeof DISTRICTS]?.map((district) => (
                      <SelectItem key={district.value} value={district.value}>
                        {district.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="ward" className="text-sm font-medium flex items-center gap-1">
                Phường/Xã 
                <span className="text-red-500">*</span>
              </Label>
              <Select value={formData.ward} onValueChange={(value) => onInputChange('ward', value)}>
                <SelectTrigger className="h-11 border-orange-200 focus:border-orange-400 focus:ring-orange-400/20">
                  <SelectValue placeholder="Chọn phường/xã" />
                </SelectTrigger>
                <SelectContent>
                  {formData.district && WARDS[formData.district as keyof typeof WARDS]?.map((ward) => (
                    <SelectItem key={ward.value} value={ward.value}>
                      {ward.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Địa chỉ chi tiết */}
          <div className="space-y-1">
            <Label htmlFor="detailedAddress" className="text-sm">Địa chỉ chi tiết *</Label>
            <Input
              id="detailedAddress"
              value={formData.detailedAddress}
              onChange={(e) => onInputChange('detailedAddress', e.target.value)}
              placeholder="Số nhà, tên đường..."
              className="h-9"
              required
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RecipientDeliverySection;